// src/components/JarvislyComponents/EnrichedEntityModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { ConfigProvider } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { createRoot } from 'react-dom/client';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import enrichedEntityStep from './Step1';

// *******************************************************************************
// THEME WRAPPER (required for modals using createRoot)
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const EnrichedEntityModal = ({
  show,
  setShow,
  afterClose,
  onSuccess,
  entityId,
  documentValue,
  documentType = 'cnpj',
  currentEngine,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedEngine, setSelectedEngine] = useState(currentEngine || 'hod');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [creditsBalance, setCreditsBalance] = useState(
    storedSubscription?.jarvislyCredits?.balance || 0
  );
  const searchActionRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Update credits balance when subscription changes
  useEffect(() => {
    if (storedSubscription?.jarvislyCredits?.balance !== undefined) {
      setCreditsBalance(storedSubscription.jarvislyCredits.balance);
    }
  }, [storedSubscription?.jarvislyCredits?.balance]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleEngineChange = useCallback(engine => {
    setSelectedEngine(engine);
  }, []);

  const handleCancel = useCallback(() => {
    setShow(false);
  }, [setShow]);

  const steps = useMemo(
    () => [
      enrichedEntityStep({
        entityId,
        documentValue,
        documentType,
        selectedEngine,
        onEngineChange: handleEngineChange,
        creditsBalance,
        isSearching,
        setIsSearching,
        searchResult,
        setSearchResult,
        searchActionRef,
        onSuccess,
      }),
    ],
    [
      entityId,
      documentValue,
      documentType,
      selectedEngine,
      handleEngineChange,
      creditsBalance,
      isSearching,
      searchResult,
      onSuccess,
    ]
  );

  const handleFinish = async () => {
    return true;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<SyncOutlined />}
      title="refresh_data_by_api"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={520}
      maskClosable={false}
      customLeftButtons={[
        { label: 'cancel', onClick: handleCancel },
      ]}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION (using createRoot pattern)
// *******************************************************************************
const showEnrichedEntityModal = ({
  entityId,
  documentValue,
  documentType = 'cnpj',
  currentEngine = 'hod',
  onSuccess,
} = {}) => {
  const container = document.createElement('div');
  container.id = 'enriched-entity-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);
  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <EnrichedEntityModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          onSuccess={onSuccess}
          entityId={entityId}
          documentValue={documentValue}
          documentType={documentType}
          currentEngine={currentEngine}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default showEnrichedEntityModal;
export { EnrichedEntityModal };
